import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { apiClient } from '../../context/AuthContext';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const ALL_PERMS = ['products', 'categories', 'pages', 'coupons', 'reviews', 'orders', 'customers', 'marketing', 'seo', 'branding', 'payments', 'security', 'users'];
const ROLES = ['admin', 'manager', 'marketing', 'support', 'custom'];
const empty = { email: '', name: '', password: '', role: 'manager', permissions: [], active: true };

const AdminUsers = () => {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(() => apiClient.get('/auth/users').then((r) => setItems(r.data)).catch((err) => { console.error('load users failed:', err); setItems([]); }), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (u) => { setEditing(u.id); setForm({ ...empty, ...u, password: '' }); setOpen(true); };
  const togglePerm = (p) => setForm({ ...form, permissions: form.permissions.includes(p) ? form.permissions.filter((x) => x !== p) : [...form.permissions, p] });
  const save = async () => {
    if (!form.email || !form.name) return toast.error('Email and name required');
    if (!editing && form.password.length < 8) return toast.error('Password must be at least 8 chars');
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, permissions: form.permissions, active: form.active };
        if (form.password) payload.password = form.password;
        await apiClient.patch(`/auth/users/${editing}`, payload);
      } else {
        await apiClient.post('/auth/users', form);
      }
      toast.success(editing ? 'Updated' : 'Created');
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const del = async (id) => { if (!window.confirm('Delete this user?')) return; try { await apiClient.delete(`/auth/users/${id}`); toast.success('Deleted'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };

  const roleColor = (r) => r === 'owner' ? 'border-[#d4af37] text-[#d4af37]' : r === 'admin' ? 'border-rose-500 text-rose-400' : r === 'manager' ? 'border-emerald-600 text-emerald-400' : 'border-zinc-700 text-zinc-300';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-zinc-400">Manage staff & permissions. Only the <span className="text-[#d4af37]">Owner</span> + users with the <span className="text-[#d4af37]">users</span> permission can access this.</p>
        <Button onClick={openNew} className="btn-gold"><Plus className="h-4 w-4 mr-2" /> New user</Button>
      </div>

      <Card className="bg-zinc-950 border-zinc-900 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60"><tr className="text-left text-zinc-400"><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Permissions</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr></thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-t border-zinc-900 hover:bg-zinc-900/30">
                <td className="p-3"><p className="text-zinc-100">{u.name}{u.id === me?.id && <span className="text-zinc-500 text-xs ml-2">(you)</span>}</p></td>
                <td className="p-3 text-zinc-300">{u.email}</td>
                <td className="p-3"><Badge variant="outline" className={roleColor(u.role)}>{u.role}</Badge></td>
                <td className="p-3"><div className="flex flex-wrap gap-1 max-w-md">{(u.effective_permissions || []).slice(0, 4).map((p) => <Badge key={p} variant="outline" className="border-zinc-800 text-zinc-400 text-xs">{p}</Badge>)}{(u.effective_permissions || []).length > 4 && <span className="text-zinc-500 text-xs">+{u.effective_permissions.length - 4}</span>}</div></td>
                <td className="p-3"><Badge variant="outline" className={u.active ? 'border-emerald-600 text-emerald-400' : 'border-rose-600 text-rose-400'}>{u.active ? 'Active' : 'Disabled'}</Badge></td>
                <td className="p-3 text-right">
                  {u.role !== 'owner' || u.id === me?.id ? <Button size="sm" variant="ghost" className="text-zinc-300 hover:text-[#d4af37]" onClick={() => openEdit(u)}><Edit className="h-4 w-4" /></Button> : null}
                  {u.role !== 'owner' && u.id !== me?.id && <Button size="sm" variant="ghost" className="text-zinc-300 hover:text-rose-500" onClick={() => del(u.id)}><Trash2 className="h-4 w-4" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-900 text-zinc-100 max-w-xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle className="serif-italic text-[#d4af37] text-2xl">{editing ? 'Edit user' : 'New user'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-transparent border-zinc-800 mt-1" /></div>
              <div><Label>Email</Label><Input type="email" disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-transparent border-zinc-800 mt-1 disabled:opacity-50" /></div>
              <div><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} disabled={form.role === 'owner'}>
                  <SelectTrigger className="bg-transparent border-zinc-800 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    {form.role === 'owner' && <SelectItem value="owner">owner</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{editing ? 'New password (leave empty to keep)' : 'Password'}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-transparent border-zinc-800 mt-1" placeholder={editing ? '••••••••' : 'Min. 8 chars'} /></div>
            </div>
            {form.role === 'custom' && (
              <div>
                <Label>Custom permissions</Label>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
                  {ALL_PERMS.map((p) => (
                    <label key={p} className={`flex items-center gap-2 border rounded px-3 py-2 cursor-pointer text-sm ${form.permissions.includes(p) ? 'border-[#d4af37] bg-[#d4af37]/10 text-[#d4af37]' : 'border-zinc-800 text-zinc-300'}`}>
                      <input type="checkbox" className="accent-[#d4af37]" checked={form.permissions.includes(p)} onChange={() => togglePerm(p)} />
                      {p}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center justify-between border border-zinc-900 rounded p-3"><span className="text-zinc-300 text-sm">Active</span><Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /></div>
          </div>
          <div className="mt-6 flex justify-end gap-3"><Button variant="outline" className="btn-outline-gold" onClick={() => setOpen(false)}>Cancel</Button><Button className="btn-gold" onClick={save}>{editing ? 'Update' : 'Create'}</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
